import { Avatar as ArkAvatar } from '@ark-ui/react';

import type { PaletteColor, Radius, Scale9 } from '../../lib/types';
import { cn } from '../../lib/utils';

type AvatarVariant = 'soft' | 'solid' | 'outline';

type AvatarProps = Omit<ArkAvatar.RootProps, 'color'> & {
	size?: Scale9;
	radius?: Radius;
	color?: PaletteColor;
	variant?: AvatarVariant;
	fallback?: React.ReactNode;
	src?: string;
	alt?: string;
};

function AvatarRoot({
	className,
	size = '2',
	radius = 'full',
	color = 'gray',
	variant = 'soft',
	fallback,
	src,
	alt,
	children,
	...props
}: AvatarProps) {
	return (
		<ArkAvatar.Root
			className={cn('pt-avatar', className)}
			data-size={size}
			data-radius={radius}
			data-color={color}
			data-variant={variant}
			{...props}
		>
			{children ?? (
				<>
					{src && <ArkAvatar.Image className="pt-avatar-image" src={src} alt={alt} />}
					<ArkAvatar.Fallback className="pt-avatar-fallback">{fallback}</ArkAvatar.Fallback>
				</>
			)}
		</ArkAvatar.Root>
	);
}

function AvatarImage({ className, ...props }: ArkAvatar.ImageProps) {
	return <ArkAvatar.Image className={cn('pt-avatar-image', className)} {...props} />;
}

function AvatarFallback({ className, ...props }: ArkAvatar.FallbackProps) {
	return <ArkAvatar.Fallback className={cn('pt-avatar-fallback', className)} {...props} />;
}

const Avatar = Object.assign(AvatarRoot, {
	Root: AvatarRoot,
	Image: AvatarImage,
	Fallback: AvatarFallback,
	Context: ArkAvatar.Context,
	RootProvider: ArkAvatar.RootProvider
});

export { Avatar, type AvatarProps };
